// @owners { team: patients-team }
import { type QueryClient } from '@tanstack/react-query';
import { getNextAvailableDateID } from '~shared/features/next-available-date/helpers/getNextAvailableDateID';
import { mergeCheckoutNextAvailableDateMappingsFromQueryClient } from '~shared/features/next-available-date/queries/mergeCheckoutNextAvailableDateMappingsFromQueryClient';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import { getNextAvailableDates } from '~shared/features/next-available-date/selectors/getNextAvailableDates';
import { getPrescriptions } from '~shared/features/prescriptions/selectors/getPrescriptions';
import { type NextAvailableDate, type Prescription, type ReduxStateShared } from '~shared/types';

type NextAvailableDateAddressIDParams = {
  nextAvailableDateAddressID: number | undefined;
};

/**
 * Maps each prescription in Redux to its next-available-date payload for the current address/facility context.
 *
 * **Strategy A:** Pass `queryClient` to read merged NAD payloads from the React Query checkout cache
 * ({@link mergeCheckoutNextAvailableDateMappingsFromQueryClient}). When `queryClient` is omitted, reads
 * `state.nextAvailableDates.dates` (Redux bridge / legacy callers such as the deprecated NAD thunk).
 */
export const getNextAvailableDatesByPrescriptionID = (
  state: ReduxStateShared,
  props: NextAvailableDateAddressIDParams,
  queryClient?: QueryClient,
): Record<number, NextAvailableDate> => {
  const addressID = props.nextAvailableDateAddressID;
  const facilityID = getNextAvailableDateFacilityID(state);
  const prescriptions = getPrescriptions(state);
  const nextAvailableDates = queryClient
    ? mergeCheckoutNextAvailableDateMappingsFromQueryClient(queryClient)
    : getNextAvailableDates(state);

  const result: Record<number, NextAvailableDate> = {};
  if (!(addressID || facilityID)) {
    return result;
  }
  prescriptions.forEach((prescription: Prescription) => {
    const nextAvailableDateID = getNextAvailableDateID({
      addressID,
      facilityID,
      prescriptionIDs: [prescription.id],
    });
    result[prescription.id] = nextAvailableDates[nextAvailableDateID];
  });
  return result;
};
